/**
 * ToolbarPopover — a portaled popover for toolbar dropdowns (the HTML
 * editor's root-folder menu, and any future action menu).
 *
 * Toolbar dropdowns can't render in-flow: the EditorToolbar's action row
 * clips horizontally (for overflow measurement) and the editor card clips
 * to its rounded bounds, so an in-flow menu gets cropped on the right /
 * bottom. This portals to <body> with fixed positioning anchored to the
 * trigger, escaping every ancestor clip. Because <body> is outside the
 * ThemedArea, it re-applies the workspace theme class so `themed-*`
 * utilities inside resolve. Closes on outside-click / Esc / scroll.
 */
import { useRef, useEffect, useState, useLayoutEffect, useCallback, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { useTheme, getThemeClass, useDismiss } from '@papan/core-ui/ui'

export function ToolbarPopover({
  anchorRef,
  open,
  onClose,
  align = 'left',
  className = 'ws-popover',
  children,
}: {
  anchorRef: RefObject<HTMLElement | null>
  open: boolean
  onClose: () => void
  /** Which edge of the popover aligns to the anchor (default left) */
  align?: 'left' | 'right'
  /** Container class for the portaled panel. Defaults to the flat `ws-popover`;
   *  pass `ws-menu` for the frost-glass context-menu look. */
  className?: string
  children: ReactNode
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const popRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<
    { top: number; left?: number; right?: number; maxWidth?: number; maxHeight: number } | null
  >(null)
  // Consumers pass a fresh onClose arrow each render; hold it in a ref so the
  // scroll/resize/RO effects subscribe once per open, not once per render.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // Position the popover under its anchor, CLAMPED to the viewport.
  //
  // The clamp is load-bearing on narrow screens: a toolbar gear sits near the
  // right edge, so a left-aligned panel (`left: r.left`) of 200px+ ran straight
  // off the side of a phone — the settings menu was half off-screen and its
  // rows unreadable. Flip/clamp so the panel always fits: pin it against the
  // right margin rather than letting it overflow, and cap its width to the
  // viewport so a wide submenu can't reintroduce the overflow.
  // Where the anchor was when the panel was last pinned to it. The scroll guard
  // compares against this, so it has to be refreshed by everything that moves
  // the anchor — a window resize reflows the toolbar and re-pins the panel
  // without closing it, and a stale rect would make the NEXT scroll look like
  // the anchor had run away.
  const anchorAt = useRef<{ top: number; left: number } | null>(null)

  const reposition = useCallback(() => {
    const a = anchorRef.current
    if (!a) return
    const r = a.getBoundingClientRect()
    anchorAt.current = { top: r.top, left: r.left }
    const M = 8 // viewport margin
    const vw = window.innerWidth
    const vh = window.innerHeight

    // VERTICAL: flip above the anchor when there isn't room below it.
    //
    // The naive form — pin under the anchor and cap the height to whatever is
    // left — computes a NON-POSITIVE max-height for an anchor in the lower part
    // of the viewport, and CSS clamps that to 0: the panel is in the DOM,
    // hit-testable, and completely invisible. That is the exact failure shape of
    // the ShareLinksDialog z-index bug this branch already fixed ("it opened
    // every time, it just never showed"). So: prefer below, flip above when
    // below is the smaller side, and only THEN cap to the space actually there.
    const spaceBelow = vh - r.bottom - 4 - M
    const spaceAbove = r.top - 4 - M
    const h = popRef.current?.offsetHeight ?? 0
    const fitsBelow = h > 0 ? h <= spaceBelow : spaceBelow >= spaceAbove
    const flipUp = !fitsBelow && spaceAbove > spaceBelow

    const top = flipUp
      ? Math.max(M, r.top - 4 - (h > 0 ? Math.min(h, spaceAbove) : spaceAbove))
      : r.bottom + 4
    // Never zero, never negative — a popover with no room is still scrollable.
    const maxHeight = Math.max(80, flipUp ? spaceAbove : spaceBelow)

    if (align === 'right') {
      // Right-aligned: keep the right edge on-screen (the anchor can sit past it
      // on a narrow viewport), and don't let the panel push off the LEFT either.
      const right = Math.max(M, Math.min(vw - r.right, vw - M))
      setPos({ top, right, maxWidth: vw - 2 * M, maxHeight })
      return
    }

    // Left-aligned: measure the rendered panel so we know when it won't fit.
    // Before the first paint we have no width — fall back to the anchor's left
    // and let the post-paint pass correct it (same frame, so no visible jump).
    const w = popRef.current?.offsetWidth ?? 0
    const left = w > 0
      ? Math.max(M, Math.min(r.left, vw - w - M))
      : r.left
    setPos({ top, left, maxWidth: vw - 2 * M, maxHeight })
  }, [align, anchorRef])

  useLayoutEffect(() => {
    if (!open) { setPos(null); return }
    reposition()
    // Second pass once the panel has a measurable width — the first pass runs
    // before it exists, so a panel that overflows can only be pulled back in
    // after it has rendered. rAF keeps it inside the same paint.
    const id = requestAnimationFrame(reposition)
    return () => cancelAnimationFrame(id)
  }, [open, reposition])

  // Outside-click + Esc. openGuard so the opening click doesn't immediately
  // close it; ignoreSelector so a portaled CHILD popover (a ToolbarPopover
  // nested inside this one — e.g. the HTML editor's root submenu opened from
  // the Settings gear) doesn't dismiss its parent. Covers BOTH container
  // classes — flat `.ws-popover` and the frost-glass `.ws-menu` variant.
  useDismiss([popRef, anchorRef], onClose, { enabled: open, openGuard: true, ignoreSelector: '.ws-popover, .ws-menu' })

  useEffect(() => {
    if (!open) return
    // A scroll under a fixed popover would leave it detached from its anchor —
    // close instead of chasing it. But a scroll inside ANY .ws-popover (this
    // one's own overflowing content, OR a nested child popover) must NOT close
    // it — otherwise long menus are unscrollable and scrolling a child menu
    // collapses its parent.
    //
    // Nor may a scroll that leaves the ANCHOR where it is: the detachment this
    // guards against cannot have happened. That case is now reachable from the
    // menu itself — a tool in here can scroll the editor's own content (the
    // image viewer re-centres the picture on every zoom step), and dismissing
    // for that makes the tool a single-shot: one click and the menu is gone.
    const onScroll = (e: Event) => {
      if ((e.target as Element)?.closest?.('.ws-popover, .ws-menu')) return
      const now = anchorRef.current?.getBoundingClientRect()
      const was = anchorAt.current
      if (was && now && now.top === was.top && now.left === was.left) return
      if (now) anchorAt.current = { top: now.top, left: now.left }
      onCloseRef.current()
    }
    // A window resize reflows the toolbar and moves the trigger; keep the
    // fixed-position popover pinned to it rather than floating away.
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open, reposition])

  // Close if the anchor is hidden while the popover is open — e.g. its editor
  // tab gets backgrounded (display:none). The portal lives under <body>, so it
  // would otherwise keep floating over the now-active tab. A ResizeObserver on
  // the anchor fires when it collapses to a 0-box (no client rects).
  useEffect(() => {
    if (!open) return
    const a = anchorRef.current
    if (!a) return
    const ro = new ResizeObserver(() => { if (!a.getClientRects().length) onCloseRef.current() })
    ro.observe(a)
    return () => ro.disconnect()
  }, [open, anchorRef])

  if (!open || !pos) return null
  return createPortal(
    <div
      ref={popRef}
      className={`${themeClass} ${className}`}
      // Positioning is intrinsic to the popover, not the container class:
      // `ws-popover` sets these in CSS but `ws-menu` (frost-glass variant)
      // does not, so pin them inline to keep either class portaling correctly.
      style={{
        position: 'fixed', zIndex: 9999,
        top: pos.top, left: pos.left, right: pos.right,
        maxWidth: pos.maxWidth,
        // Computed against the side we actually placed on (see reposition) —
        // never a negative `calc()`, which CSS clamps to 0 and renders invisible.
        maxHeight: pos.maxHeight,
        overflowY: 'auto',
      }}
    >
      {children}
    </div>,
    document.body,
  )
}
